import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";
import {
  hasConsentDecision,
  grantConsent,
  denyConsent,
} from "@/lib/analytics";

const PANEL_MOTION = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: { duration: 0.35, ease: "easeOut" },
};

export default function ConsentBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show only if no decision has been made yet.
    // Slight delay so it doesn't compete with the Girribal Gate.
    const t = setTimeout(() => {
      if (!hasConsentDecision()) setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const handleAccept = () => {
    grantConsent();
    setOpen(false);
  };
  const handleDecline = () => {
    denyConsent();
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          {...PANEL_MOTION}
          data-testid="consent-banner"
          className="fixed bottom-4 left-4 right-4 z-[60] md:left-6 md:right-auto md:max-w-md"
        >
          <div className="relative bg-stone-950 text-stone-100 border border-stone-800 shadow-2xl p-5 md:p-6">
            <button
              type="button"
              onClick={handleDecline}
              aria-label="Decline analytics"
              data-testid="consent-dismiss"
              className="absolute top-3 right-3 text-stone-500 hover:text-stone-200 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="h-4 w-4 text-[#E4B58E]" />
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#E4B58E]">
                Your privacy
              </div>
            </div>

            <h2 className="font-display text-lg md:text-xl tracking-tight font-bold leading-snug">
              We respect Indigenous Data Sovereignty.
            </h2>

            <p className="mt-3 text-sm text-stone-300 leading-relaxed">
              We'd like to use Google Analytics — anonymised, IP-scrambled, no advertising — to understand which procurement teams find this site useful. Nothing personal is collected.
            </p>

            <p className="mt-3 text-[11px] text-stone-400 leading-relaxed">
              No tracking happens until you choose. You can change this any time from the Privacy link in the footer.
            </p>

            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleAccept}
                data-testid="consent-accept"
                className="flex-1 bg-[#E4B58E] text-stone-950 px-4 py-2.5 text-sm font-semibold rounded-sm hover:bg-[#F2C9A4] transition-colors"
              >
                Accept analytics
              </button>
              <button
                type="button"
                onClick={handleDecline}
                data-testid="consent-decline"
                className="flex-1 border border-stone-700 text-stone-100 px-4 py-2.5 text-sm font-medium rounded-sm hover:border-stone-500 transition-colors"
              >
                Decline
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
